import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  BanknotesIcon, CheckCircleIcon, XCircleIcon, ClockIcon,
} from '@heroicons/react/24/outline';
import api from '../../api/client';

// Admin queue for cashout requests. Staff creates a request → lands
// here → admin approves (deducts points + sends to Payroll), denies
// (with reason), or marks paid after cutting the check.
//
// Three sections: pending (needs action), approved (waiting on
// payout), and history (last 30 days paid/denied for reference).

const dollars = (cents) => `$${(Number(cents) / 100).toFixed(2)}`;
const fmt = (d) => d ? new Date(d).toLocaleString() : '—';

export default function WalletApprovalsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/wallet/cashouts');
      setRows(data);
    } catch { toast.error('Could not load cashouts'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    if (!window.confirm(
      `Approve ${r.first_name} ${r.last_name}'s cashout — ${r.points_redeemed} points → ${dollars(r.cash_amount_cents)}?\n\n` +
      `Points will be deducted immediately. Payroll gets notified separately.`
    )) return;
    setBusyId(r.id);
    try {
      await api.post(`/wallet/cashouts/${r.id}/approve`);
      toast.success('Approved');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Approve failed');
    } finally { setBusyId(null); }
  };

  const deny = async (r) => {
    const reason = window.prompt(
      `Deny ${r.first_name} ${r.last_name}'s cashout? Reason (will be visible to them):`
    );
    if (!reason || !reason.trim()) return;
    setBusyId(r.id);
    try {
      await api.post(`/wallet/cashouts/${r.id}/deny`, { reason: reason.trim() });
      toast.success('Denied');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Deny failed');
    } finally { setBusyId(null); }
  };

  const markPaid = async (r) => {
    if (!window.confirm(`Mark ${r.first_name} ${r.last_name}'s ${dollars(r.cash_amount_cents)} cashout as PAID?`)) return;
    setBusyId(r.id);
    try {
      await api.post(`/wallet/cashouts/${r.id}/paid`);
      toast.success('Marked paid');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Mark paid failed');
    } finally { setBusyId(null); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;

  const pending  = rows.filter(r => r.status === 'pending');
  const approved = rows.filter(r => r.status === 'approved');
  const history  = rows.filter(r => r.status === 'paid' || r.status === 'denied').slice(0, 30);

  return (
    <div className="animate-slide-up max-w-4xl mx-auto">
      <div className="mb-6 flex items-start gap-4">
        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <BanknotesIcon className="w-6 h-6 text-orange-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cashout Approvals</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Staff-requested cashouts from the Points Wallet.
            {pending.length > 0 && <> <strong>{pending.length}</strong> waiting on you.</>}
          </p>
        </div>
      </div>

      <Section title="Waiting on approval" empty="No pending cashouts.">
        {pending.map(r => (
          <Row key={r.id} r={r} busy={busyId === r.id} tone="yellow">
            <button onClick={() => deny(r)} disabled={busyId === r.id}
              className="text-xs font-medium text-red-700 hover:bg-red-50 border border-red-200 hover:border-red-300 rounded-lg px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-50">
              <XCircleIcon className="w-3.5 h-3.5" /> Deny
            </button>
            <button onClick={() => approve(r)} disabled={busyId === r.id}
              className="text-xs font-semibold bg-green-500 hover:bg-green-600 text-white rounded-lg px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-50">
              <CheckCircleIcon className="w-3.5 h-3.5" />
              {busyId === r.id ? 'Approving…' : 'Approve'}
            </button>
          </Row>
        ))}
      </Section>

      <Section title="Approved — awaiting payout" empty="Nothing waiting on payment.">
        {approved.map(r => (
          <Row key={r.id} r={r} busy={busyId === r.id} tone="blue"
               subline={`Approved ${fmt(r.approved_at)}${r.approved_by_name ? ` by ${r.approved_by_name}` : ''}`}>
            <button onClick={() => markPaid(r)} disabled={busyId === r.id}
              className="text-xs font-semibold bg-blue-500 hover:bg-blue-600 text-white rounded-lg px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-50">
              <ClockIcon className="w-3.5 h-3.5" /> Mark paid
            </button>
          </Row>
        ))}
      </Section>

      <Section title="Recent history" empty="No completed cashouts yet.">
        {history.map(r => (
          <Row key={r.id} r={r} tone={r.status === 'paid' ? 'green' : 'gray'}
               subline={r.status === 'paid'
                 ? `Paid ${fmt(r.paid_at)}`
                 : `Denied — ${r.notes || 'no reason'}`}>
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
              r.status === 'paid' ? 'bg-green-50 text-green-700 border-green-200'
                                  : 'bg-gray-50 text-gray-600 border-gray-200'}`}>
              {r.status.toUpperCase()}
            </span>
          </Row>
        ))}
      </Section>
    </div>
  );
}

function Section({ title, empty, children }) {
  const kids = Array.isArray(children) ? children : [children];
  const hasKids = kids.filter(Boolean).length > 0;
  return (
    <div className="mb-6">
      <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-2">{title}</h2>
      {hasKids ? <div className="space-y-2">{children}</div>
                : <div className="bg-white border border-gray-200 rounded-2xl p-5 text-sm text-gray-500 text-center">{empty}</div>}
    </div>
  );
}

function Row({ r, busy, tone = 'yellow', subline, children }) {
  const borderColor = ({
    yellow: 'border-l-yellow-400',
    blue:   'border-l-blue-400',
    green:  'border-l-green-400',
    gray:   'border-l-gray-300',
  })[tone] || 'border-l-gray-300';
  return (
    <div className={`bg-white border border-gray-200 rounded-2xl p-4 border-l-4 ${borderColor}`}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Link to={`/staff/${r.user_id}`} className="text-sm font-semibold text-gray-900 hover:text-orange-600">
            {r.first_name} {r.last_name}
          </Link>
          <p className="text-xs text-gray-500 mt-0.5">
            {r.points_redeemed} pts → <strong>{dollars(r.cash_amount_cents)}</strong>
            {' '}· Requested {fmt(r.requested_at)}
          </p>
          {subline && <p className="text-[11px] text-gray-400 mt-0.5">{subline}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {children}
        </div>
      </div>
    </div>
  );
}
